import { useEffect, useMemo, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { attachRouteEditor } from './routeEditor.js';
import { attachRouteArrows } from './routeArrows.js';

/**
 * Declarative wrapper around Leaflet.
 *
 * Written by hand rather than pulled from react-leaflet, which ships under the
 * Hippocratic License and would break the "permissive licences only" rule.
 * Leaflet itself is BSD-2-Clause.
 *
 * The contract: props describe what should be on the map, and each effect
 * reconciles one layer family by id. Layers are reused across renders and only
 * the ones whose payload actually changed are rebuilt, so panning and zooming
 * stay smooth while the sidebar re-renders.
 */

// Fallback only. The real basemap config arrives from /api/meta via the `tiles`
// prop, so the server stays the single source of truth for it.
const DEFAULT_TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const DEFAULT_TILE_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> | itineraires <a href="https://project-osrm.org/">OSRM</a>';

const FRANCE = [46.6, 2.5];

/** Build the HTML for a pin. Kept as a string so Leaflet can use a divIcon. */
function pinHtml({ kind, color, glyph, label, dimmed }) {
  const cls = ['pin', `pin-${kind}`, dimmed ? 'pin-dim' : ''].filter(Boolean).join(' ');
  const style = color ? ` style="background:${color}"` : '';
  const text = glyph == null ? '' : `<span>${glyph}</span>`;
  const tag = label ? `<span class="marker-label">${label}</span>` : '';
  return `<div class="${cls}"${style}>${text}</div>${tag}`;
}

const iconFor = (marker) =>
  L.divIcon({
    className: 'leaflet-div-icon-plain',
    html: pinHtml(marker),
    iconSize: marker.kind === 'dest' ? [38, 38] : marker.kind === 'stop' ? [22, 22] : [28, 28],
    iconAnchor: marker.kind === 'dest' ? [19, 19] : marker.kind === 'stop' ? [11, 11] : [14, 14],
  });

export default function MapCanvas({
  markers = [],
  routes = [],
  fitKey = null,
  fitBounds = null,
  picking = false,
  onMapClick,
  onSelect,
  center = FRANCE,
  zoom = 6,
  interactive = true,
  className = '',
  tiles = null,
  routeEditor = null,
  arrowRoutes = null,
  onBackgroundTap,
  onInteract,
  framedByPanel = false,
  focusOn = null,
}) {
  const hostRef = useRef(null);
  const mapRef = useRef(null);
  const tileLayerRef = useRef(null);
  const markerLayers = useRef(new Map());
  const routeLayers = useRef(new Map());
  const clickRef = useRef(onMapClick);
  const selectRef = useRef(onSelect);
  const pickingRef = useRef(picking);
  const tapRef = useRef(onBackgroundTap);
  const interactRef = useRef(onInteract);
  tapRef.current = onBackgroundTap;
  interactRef.current = onInteract;
  clickRef.current = onMapClick;
  selectRef.current = onSelect;
  pickingRef.current = picking;

  /* --- create once ------------------------------------------------------- */
  useEffect(() => {
    const map = L.map(hostRef.current, {
      center,
      zoom,
      zoomControl: false, // added below, positioned away from the floating top bar
      attributionControl: true,
      dragging: interactive,
      scrollWheelZoom: interactive,
      doubleClickZoom: interactive,
      touchZoom: interactive,
      boxZoom: interactive,
      keyboard: interactive,
      preferCanvas: true, // many polyline points; canvas beats SVG here
    });

    tileLayerRef.current = L.tileLayer(DEFAULT_TILE_URL, {
      maxZoom: 19,
      attribution: DEFAULT_TILE_ATTRIBUTION,
      className: 'map-tiles',
    }).addTo(map);

    if (interactive) L.control.zoom({ position: 'topright' }).addTo(map);
    /**
     * Leaflet's map click fires for a genuine tap on the background only: a pan
     * is not a click, and a tap on a marker is consumed by that marker. So this
     * is exactly "the person tapped the map itself", which is what the layout
     * toggle wants to hear.
     */
    map.on('click', (e) => {
      clickRef.current?.({ lat: e.latlng.lat, lng: e.latlng.lng });
      tapRef.current?.();
    });

    /**
     * "The person is working with this map." Used by the compact map on the
     * create form, which is too small to navigate and grows while in use.
     *
     * Deliberately not Leaflet's movestart/zoomstart: those also fire for the
     * framing the app does itself, so the map would come up already expanded and
     * never shrink. A pointer landing on the container covers a tap, the start of
     * a pan and the zoom buttons; the wheel covers the rest. All of them are the
     * person, none of them are us.
     */
    const noteInteraction = () => interactRef.current?.();
    hostRef.current.addEventListener('pointerdown', noteInteraction, { passive: true });
    hostRef.current.addEventListener('wheel', noteInteraction, { passive: true });

    mapRef.current = map;

    // The panel animates in beside the map; recompute size once it settles.
    const observer = new ResizeObserver(() => map.invalidateSize({ animate: false }));
    observer.observe(hostRef.current);

    return () => {
      observer.disconnect();
      hostRef.current?.removeEventListener('pointerdown', noteInteraction);
      hostRef.current?.removeEventListener('wheel', noteInteraction);
      /**
       * Settle any animation before tearing the map down.
       *
       * A zoom still running at removal fires its transition-end handler later,
       * against panes that no longer exist, and throws. It is easy to hit: close
       * a form while its map is still easing into place. Leaflet exposes no way
       * to cancel a zoom transition, so the running one is ended by hand.
       */
      try {
        map.stop();
        if (map._animatingZoom) map._onZoomTransitionEnd();
      } catch {
        /* already torn down */
      }
      map.remove();
      mapRef.current = null;
      markerLayers.current.clear();
      routeLayers.current.clear();
    };
    // Intentionally mount-only: later prop changes are handled by the effects below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* --- markers ----------------------------------------------------------- */
  // Serialised so a re-render with identical markers does no DOM work at all.
  const markerKey = useMemo(
    () =>
      markers
        .map((m) => `${m.id}:${m.lat}:${m.lng}:${m.kind}:${m.color}:${m.glyph}:${m.label}:${m.dimmed ? 1 : 0}`)
        .join('|'),
    [markers],
  );

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const live = markerLayers.current;
    const seen = new Set();

    for (const marker of markers) {
      if (marker.lat == null || marker.lng == null) continue;
      seen.add(marker.id);
      const existing = live.get(marker.id);
      if (existing) {
        existing.layer.setLatLng([marker.lat, marker.lng]);
        if (existing.signature !== marker.signature) existing.layer.setIcon(iconFor(marker));
        existing.signature = marker.signature;
        existing.rideId = marker.rideId;
        existing.place = marker.place;
      } else {
        const layer = L.marker([marker.lat, marker.lng], {
          icon: iconFor(marker),
          zIndexOffset: marker.kind === 'dest' ? 1000 : marker.kind === 'stop' ? 0 : 400,
          keyboard: false,
          riseOnHover: true,
        }).addTo(map);
        layer.on('click', (e) => {
          L.DomEvent.stopPropagation(e);
          const entry = live.get(marker.id);
          /**
           * A marker swallows the click before the map ever sees it, which is why
           * trying to place a stop on the meeting point used to do nothing at all
           * and forced people to zoom in and aim beside it. While placing a point,
           * a marker is a target: snap to it exactly and reuse its name, which is
           * a better answer than whatever reverse geocoding would invent there.
           */
          if (pickingRef.current) {
            const at = layer.getLatLng();
            clickRef.current?.({ lat: at.lat, lng: at.lng, place: entry?.place ?? null });
            return;
          }
          if (entry?.rideId) selectRef.current?.(entry.rideId);
        });
        live.set(marker.id, { layer, signature: marker.signature, rideId: marker.rideId, place: marker.place });
      }
    }

    for (const [id, entry] of live) {
      if (seen.has(id)) continue;
      entry.layer.remove();
      live.delete(id);
    }
  }, [markerKey, markers]);

  /* --- routes ------------------------------------------------------------ */
  const routeKey = useMemo(
    () => routes.map((r) => `${r.id}:${r.color}:${r.dimmed ? 1 : 0}:${r.selected ? 1 : 0}:${r.points.length}:${r.dashed ? 1 : 0}`).join('|'),
    [routes],
  );

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const live = routeLayers.current;
    const seen = new Set();

    for (const route of routes) {
      if (!route.points?.length) continue;
      seen.add(route.id);
      const style = {
        color: route.color,
        weight: route.selected ? 5.5 : 3.5,
        opacity: route.dimmed ? 0.22 : route.selected ? 0.95 : 0.82,
        dashArray: route.dashed ? '7 8' : undefined,
        lineJoin: 'round',
        lineCap: 'round',
      };
      const existing = live.get(route.id);
      if (existing) {
        if (existing.count !== route.points.length) existing.layer.setLatLngs(route.points);
        existing.layer.setStyle(style);
        existing.count = route.points.length;
      } else {
        const layer = L.polyline(route.points, { ...style, interactive: Boolean(route.rideId) }).addTo(map);
        if (route.rideId) {
          layer.on('click', (e) => {
            L.DomEvent.stopPropagation(e);
            selectRef.current?.(route.rideId);
          });
        }
        live.set(route.id, { layer, count: route.points.length });
      }
      // Keep the highlighted itinerary above the others.
      if (route.selected) live.get(route.id).layer.bringToFront();
    }

    for (const [id, entry] of live) {
      if (seen.has(id)) continue;
      entry.layer.remove();
      live.delete(id);
    }
  }, [routeKey, routes]);

  /* --- viewport ---------------------------------------------------------- */
  // Driven by fitKey so the map only re-frames on a deliberate change, never
  // fighting the member while they pan.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !fitBounds?.length) return;
    const bounds = L.latLngBounds(fitBounds);
    if (!bounds.isValid()) return;
    /**
     * Frame the route into the part of the map that is actually visible.
     *
     * The panel floats over the map, so estimated padding leaves content beneath
     * it. On a phone the bottom sheet covers most of the screen, and a route
     * fitted to the full map put its drag handles behind the sheet where no
     * finger could reach them. Measure the panel rather than guessing at it.
     *
     * fitBounds, not flyToBounds: the "fly" animation zooms out and back in, and
     * every intermediate zoom level fetches a fresh screenful of tiles. On a
     * cross-country route that was ~300 requests per view change, which is a lot
     * to ask of volunteer-run tile servers for one reframing.
     */
    const mapRect = hostRef.current.getBoundingClientRect();
    // Only the full-bleed map has a panel floating over it; an inline map in a
    // form is its own box and must not reserve room for something elsewhere.
    const panelRect = framedByPanel ? document.querySelector('.panel')?.getBoundingClientRect() : null;
    const sidePanel = window.matchMedia('(min-width: 901px)').matches;

    let paddingTopLeft = [28, 84];
    let paddingBottomRight = [28, 44];
    if (panelRect?.width) {
      if (sidePanel) {
        paddingTopLeft = [Math.max(28, panelRect.right - mapRect.left + 28), 84];
        paddingBottomRight = [72, 60];
      } else {
        // Bottom sheet: keep the whole route above its top edge.
        paddingBottomRight = [28, Math.max(44, mapRect.bottom - panelRect.top + 28)];
      }
    }

    map.fitBounds(bounds, { paddingTopLeft, paddingBottomRight, maxZoom: 14, animate: true, duration: 0.5 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitKey]);

  useEffect(() => {
    const map = mapRef.current;
    if (map) map.getContainer().style.cursor = picking ? 'crosshair' : '';
  }, [picking]);

  /**
   * Move the view somewhere, without choosing anything there. Driven by a nonce
   * so searching the same place twice still moves the map, and separate from
   * fitBounds because framing the content and going somewhere on request are
   * different intentions.
   */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !focusOn) return;
    if (focusOn.bbox) {
      const [south, north, west, east] = focusOn.bbox;
      const bounds = L.latLngBounds([south, west], [north, east]);
      if (bounds.isValid()) {
        map.flyToBounds(bounds, { padding: [26, 26], maxZoom: 16, duration: 0.7 });
        return;
      }
    }
    if (focusOn.lat == null) return;

    /**
     * Where the point should land on screen, relative to the container centre.
     *
     * The panel floats over the map, so the middle of the container is not the
     * middle of what anyone can see. Flying to the plain centre parks the point
     * behind the panel, which is the one place it is of no use.
     */
    const offset = (() => {
      if (!framedByPanel || !hostRef.current) return null;
      const panel = document.querySelector('.panel')?.getBoundingClientRect();
      if (!panel?.width) return null;
      const mapRect = hostRef.current.getBoundingClientRect();
      if (window.matchMedia('(min-width: 901px)').matches) {
        const covered = Math.max(0, panel.right - mapRect.left);
        return covered > 12 ? L.point(covered / 2, 0) : null;
      }
      const covered = Math.max(0, mapRect.bottom - panel.top);
      return covered > 12 ? L.point(0, -covered / 2) : null;
    })();

    const zoom = focusOn.zoom ?? 15;
    const target = L.latLng(focusOn.lat, focusOn.lng);
    // screen(P) = centre + (project(P) - project(C)), so project(C) = project(P) - offset.
    const centre = offset ? map.unproject(map.project(target, zoom).subtract(offset), zoom) : target;
    map.flyTo(centre, zoom, { duration: 0.7 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusOn?.nonce]);

  /** Direction arrows. Re-attached when the lines themselves change. */
  const arrowKey = useMemo(
    () => (arrowRoutes ?? []).map((r) => `${r.id}:${r.color}:${r.points.length}`).join('|'),
    [arrowRoutes],
  );

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !arrowRoutes?.length) return undefined;
    return attachRouteArrows(map, arrowRoutes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrowKey]);

  /**
   * Route reshaping. Re-attached whenever the geometry or the shaping points
   * change, because the editor closes over both to decide which leg a grab
   * belongs to; a stale copy would anchor points to the wrong part of the trip.
   */
  const editorKey = routeEditor
    ? `${routeEditor.rideId}:${routeEditor.points.length}:${routeEditor.via.map((v) => `${v.id}@${v.lat},${v.lng}`).join('|')}`
    : null;

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !routeEditor) return undefined;
    return attachRouteEditor(map, routeEditor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorKey]);

  // The basemap is configured server-side and arrives after the first paint.
  useEffect(() => {
    const layer = tileLayerRef.current;
    if (!layer || !tiles?.url || tiles.url === layer._url) return;
    layer.setUrl(tiles.url);
    if (tiles.attribution) {
      mapRef.current?.attributionControl.removeAttribution(DEFAULT_TILE_ATTRIBUTION);
      mapRef.current?.attributionControl.addAttribution(tiles.attribution);
    }
  }, [tiles?.url, tiles?.attribution]);

  // A blocked tile server answers HTTP 200 with a placeholder image, so nothing
  // fails loudly. Say plainly what happened instead of leaving a broken-looking map.
  const tileProblem = tiles?.status && tiles.status.ok === false ? tiles.status : null;

  return (
    <div ref={hostRef} className={`map-root ${picking ? 'picking' : ''} ${className}`} aria-label="Carte des trajets">
      {tileProblem && (
        <div className="map-warning" role="status">
          <strong>Fond de carte indisponible.</strong> {tileProblem.message} Voir la section
          « Maps and routing » du README pour changer de fournisseur de tuiles.
        </div>
      )}
    </div>
  );
}
